import { useState, useEffect } from 'react';
import styles from './Notifications.module.css';
import {
  notificationService,
  CreateNotificationInput,
  NotificationResponse,
  UpdateNotificationInput,
} from '../../services/notification.service';




const Notifications = () => {
  const [notifications, setNotifications] = useState<NotificationResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateNotificationInput>({
    title: '',
    content: '',
    scopeType: 'all_users',
    scopeData: null,
    isActive: true,
    validityStart: null,
    validityEnd: null,
    channel: 'popup',
  });

  // Separate state for scopeData inputs
  const [userIds, setUserIds] = useState<string>('');
  const [roleName, setRoleName] = useState<string>('');
  const [parentUserId, setParentUserId] = useState<string>('');

  // Fetch notifications on component mount
  useEffect(() => {
    fetchNotifications();
  }, []);

  // Reset scopeData inputs when scopeType changes
  // useEffect(() => {
  //   setUserIds('');
  //   setRoleName('');
  //   setParentUserId('');
  //   setFormData(prev => ({
  //     ...prev,
  //     scopeData: null,
  //   }));
  // }, [formData.scopeType]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await notificationService.getNotifications();
      console.log({data});
      setNotifications(data);
    } catch (err: any) {
      console.error('Failed to fetch notifications:', err);
      setError(err.message || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      // Construct proper scopeData based on scopeType
      let scopeData: any = null;
      
      switch (formData.scopeType) {
        case 'all_users':
          scopeData = null;
          break;
        case 'selected_users':
          if (userIds.trim()) {
            const userIdArray = userIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
            scopeData = { userIds: userIdArray };
          }
          break;
        case 'by_role':
          if (roleName) {
            scopeData = { roleName };
          }
          break;
        case 'hierarchy':
          if (parentUserId.trim()) {
            const userId = parseInt(parentUserId.trim());
            if (!isNaN(userId)) {
              scopeData = { userId };
            }
          }
          break;
      }

      const submitData = {
        ...formData,
        scopeData,
      };

      console.log('Submitting notification with data:', JSON.stringify(submitData, null, 2));

      if (editingId) {
        // Update existing notification - exclude 'type' and 'scopeType' fields
        const { type, scopeType,scopeData, ...updateData } = submitData;
        await notificationService.updateNotification(editingId, updateData);
      } else {
        // Create new notification
        await notificationService.createNotification(submitData);
      }

      // Reset form and refresh list
      resetForm();
      await fetchNotifications();
    } catch (err: any) {
      console.error('Failed to save notification:', err);
      setError(err.message || 'Failed to save notification');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (notification: NotificationResponse) => {
    console.log('handleEdit', notification.scopeType, {notification}, notification.scopeData);
    setFormData({
      title: notification.title,
      content: notification.content,
      type: notification.type || undefined,
      scopeType: notification.scopeType as any,
      scopeData: notification.scopeData,
      isActive: notification.isActive,
      validityStart: notification.validityStart,
      validityEnd: notification.validityEnd,
      channel: notification.channel,
    });

    // Populate scopeData input fields based on notification data
    if (notification.scopeData) {
      // Parse scopeData if it's a string
      let scopeData: any;
      if (typeof notification.scopeData === 'string') {
        try {
          scopeData = JSON.parse(notification.scopeData);
        } catch (e) {
          console.error('Failed to parse scopeData:', e);
          scopeData = null;
        }
      } else {
        scopeData = notification.scopeData;
      }

      if (scopeData) {
        if (notification.scopeType === 'selected_users' && scopeData.userIds) {
          setUserIds(Array.isArray(scopeData.userIds) ? notification.scopeData.userIds.join(', ') : notification.scopeData.userIds);
        } else if (notification.scopeType === 'by_role' && notification.scopeData.roleName) {
          setRoleName(notification.scopeData.roleName);
        } else if (notification.scopeType === 'hierarchy' && notification?.scopeData?.userId) {
          console.log(notification.scopeData?.userId?.toString());
          setParentUserId(notification.scopeData?.userId?.toString());
        }
      }
    }

    setEditingId(notification.id);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to inactivate this notification?')) {
      return;
    }

    try {
      setError(null);
      await notificationService.deleteNotification(id);
      // Update local state instead of refetching
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, isActive: false } : n)
      );
    } catch (err: any) {
      console.error('Failed to inactivate notification:', err);
      setError(err.message || 'Failed to inactivate notification');
    }
  };

  const handleActivate = async (id: number) => {
    if (!window.confirm('Are you sure you want to activate this notification?')) {
      return;
    }

    try {
      setError(null);
      await notificationService.updateNotification(id, { isActive: true });
      // Update local state instead of refetching
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, isActive: true } : n)
      );
    } catch (err: any) {
      console.error('Failed to activate notification:', err);
      setError(err.message || 'Failed to activate notification');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      scopeType: 'all_users',
      scopeData: null,
      isActive: true,
      validityStart: null,
      validityEnd: null,
    });
    setUserIds('');
    setRoleName('');
    setParentUserId('');
    setEditingId(null);
    setShowForm(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value || null,
    }));
  };

  return (
    <main className={styles.main}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>Notifications Management</h1>
          <button
            className={styles.btnPrimary}
            onClick={() => {
              if (showForm) {
                resetForm();
              } else {
                resetForm();
                setShowForm(true);
              }
            }}
            disabled={loading}
          >
            {showForm ? 'Cancel' : 'Create New Notification'}
          </button>
        </div>
        {error && (
          <div className={styles.error}>
            {error}
          </div>
        )}
        {showForm && (
          <form className={styles.form} onSubmit={handleSubmit}>
            <h2>{editingId ? 'Edit Notification' : 'Create New Notification'}</h2>
            <div className={styles.formGroup}>
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="content">Content *</label>
              <textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                required
                disabled={loading}
                rows={4}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="channel">Popup type *</label>
              <select
                id="channel"
                name="channel"
                value={formData.channel}
                onChange={handleInputChange}
                required
                disabled={loading}
              >
                <option value="popup">Popup</option>
                <option value="push">Push</option>
                <option value="instant">Instant</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="scopeType">For *</label>
              <select
                id="scopeType"
                name="scopeType"
                value={formData.scopeType}
                onChange={handleInputChange}
                required
                disabled={loading}
              >
                <option value="all_users">All Users</option>
                <option value="selected_users">Selected Users</option>
                <option value="by_role">By Role</option>
                <option value="hierarchy">Hierarchy</option>
              </select>
            </div>
            
            {formData.scopeType === 'selected_users' && (
              <div className={styles.formGroup}>
                <label htmlFor="userIds">User IDs (comma-separated) *</label>
                <input
                  type="text"
                  id="userIds"
                  name="userIds"
                  value={userIds}
                  onChange={(e) => setUserIds(e.target.value)}
                  disabled={loading}
                  placeholder="e.g., 19, 25, 30"
                  required
                />
              </div>
            )}
            {formData.scopeType === 'by_role' && (
              <div className={styles.formGroup}>
                <label htmlFor="roleName">Role Name *</label>
                <select
                  id="roleName"
                  name="roleName"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  disabled={loading}
                  required
                >
                  <option value="">Select a role</option>
                  <option value="ADMIN">Admin</option>
                  <option value="USER">User</option>
                  <option value="DISTRIBUTOR">Distributor</option>
                  <option value="SUBDISTRIBUTOR">Sub-Distributor</option>
                  <option value="AGENT">Agent</option>
                  <option value="SHOP">Shop</option>
                </select>
              </div>
            )}
            {formData.scopeType === 'hierarchy' && (
              <div className={styles.formGroup}>
                <label htmlFor="parentUserId">Parent User ID *</label>
                <input
                  type="number"
                  id="parentUserId"
                  name="parentUserId"
                  value={parentUserId}
                  onChange={(e) => setParentUserId(e.target.value)}
                  disabled={loading}
                  placeholder="e.g., 1"
                  required
                />
              </div>
            )}

            <div className={styles.formGroup}>
              <label htmlFor="validityStart">Validity Start</label>
              <input
                type="datetime-local"
                id="validityStart"
                name="validityStart"
                value={formData.validityStart ? new Date(formData.validityStart).toISOString().slice(0, 16) : ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  validityStart: e.target.value ? new Date(e.target.value).toISOString() : null
                }))}
                disabled={loading}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="validityEnd">Validity End</label>
              <input
                type="datetime-local"
                id="validityEnd"
                name="validityEnd"
                value={formData.validityEnd ? new Date(formData.validityEnd).toISOString().slice(0, 16) : ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  validityEnd: e.target.value ? new Date(e.target.value).toISOString() : null
                }))}
                disabled={loading}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                Active
              </label>
            </div>

            <div className={styles.formActions}>
              <button
                type="submit"
                className={styles.btnPrimary}
                disabled={loading}
              >
                {loading ? 'Saving...' : (editingId ? 'Update' : 'Create')}
              </button>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={resetForm}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className={styles.notificationsList}>
          <h2>Notifications List</h2>
          {loading && !showForm ? (
            <div className={styles.loading}>Loading notifications...</div>
          ) : notifications.length === 0 ? (
            <div className={styles.empty}>No notifications found</div>
          ) : (
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Title</th>
                    <th>Content</th>
                    <th>Type</th>
                    <th>Scope</th>
                    <th>Status</th>
                    <th>Validity</th>
                    <th>Created By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {notifications.map((notification) => (
                    <tr key={notification.id}>
                      <td>{notification.id}</td>
                      <td>{notification.title}</td>
                      <td className={styles.contentCell}>{notification.content}</td>
                      <td>{notification.type || '-'}</td>
                      <td>{notification.scopeType}</td>
                      <td>
                        <span className={notification.isActive ? styles.statusActive : styles.statusInactive}>
                          {notification.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        {notification.validityStart && notification.validityEnd ? (
                          <>
                            <div>{new Date(notification.validityStart).toLocaleDateString()}</div>
                            <div>to {new Date(notification.validityEnd).toLocaleDateString()}</div>
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>{notification.createdByUser?.username || '-'}</td>
                      <td>
                        <div className={styles.actions}>
                          <button
                            className={styles.btnEdit}
                            onClick={() => handleEdit(notification)}
                            disabled={loading}
                          >
                            Edit
                          </button>
                          {notification.isActive ? (
                            <button
                              className={styles.btnDelete}
                              onClick={() => handleDelete(notification.id)}
                              disabled={loading}
                            >
                              Inactivate
                            </button>
                          ) : (
                            <button
                              className={styles.btnPrimary2}
                              onClick={() => handleActivate(notification.id)}
                              disabled={loading}
                            >
                              &nbsp;Activate&nbsp;
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default Notifications;
